import apiClient, { createClientRequestId, logApiJson } from '../apiService';

const getRefreshToken = () => localStorage.getItem('refreshToken');

const buildKycDocumentsFormData = (files, documentTypeId, options = {}) => {
    if (files instanceof FormData) return files;

    const formData = new FormData();

    if (files instanceof FileList || Array.isArray(files)) {
        for (let i = 0; i < files.length; i++) {
            formData.append('documents', files[i]);
        }
    } else if (files instanceof File) {
        formData.append('documents', files);
    } else {
        throw new Error('Invalid file input');
    }

    if (documentTypeId) formData.append('documentTypeId', documentTypeId.toString());
    if (options.documentNumber) formData.append('documentNumber', options.documentNumber);
    if (options.region) formData.append('region', options.region.toUpperCase());
    if (options.subjectType) formData.append('subjectType', options.subjectType);
    if (options.subjectName) formData.append('subjectName', options.subjectName);
    if (options.subjectIndex) formData.append('subjectIndex', String(options.subjectIndex));
    if (options.documentSide) formData.append('documentSide', options.documentSide);
    if (options.instanceKey) formData.append('instanceKey', options.instanceKey);

    return formData;
};

const isBrowserFile = (value) => typeof File !== 'undefined' && value instanceof File;
const isBrowserFileList = (value) => typeof FileList !== 'undefined' && value instanceof FileList;
const JOB_IMPORT_UPLOAD_TIMEOUT_MS = Number(process.env.REACT_APP_JOB_IMPORT_UPLOAD_TIMEOUT_MS || 120000);
const REGISTRATION_UPLOAD_TIMEOUT_MS = Number(process.env.REACT_APP_REGISTRATION_UPLOAD_TIMEOUT_MS || 120000);

const appendOfferField = (formData, key, value) => {
    if (value === undefined || value === null || value === '') return;

    if (isBrowserFile(value)) {
        formData.append(key, value);
        return;
    }

    if (key === 'additionalDocuments' && (Array.isArray(value) || isBrowserFileList(value))) {
        Array.from(value).forEach((file) => {
            if (isBrowserFile(file)) formData.append('additionalDocuments', file);
        });
        return;
    }

    if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
        return;
    }

    formData.append(key, String(value));
};

const buildOfferFormData = (offerData = {}) => {
    if (offerData instanceof FormData) return offerData;

    const formData = new FormData();
    Object.entries(offerData).forEach(([key, value]) => appendOfferField(formData, key, value));
    return formData;
};

const buildSupportTicketFormData = (payload = {}, attachment = null) => {
    if (payload instanceof FormData) return payload;

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => appendOfferField(formData, key, value));
    if (attachment) formData.append('attachment', attachment);
    return formData;
};

const buildRegistrationPayload = (userData = {}) => {
    if (!isBrowserFile(userData.companyProfileDocument)) return userData;

    const formData = new FormData();
    Object.entries(userData).forEach(([key, value]) => {
        if (key === 'companyProfileDocument') {
            formData.append(key, value);
        } else if (value !== undefined && value !== null && value !== '') {
            formData.append(key, String(value));
        }
    });
    return formData;
};

// Auth API
export const authAPI = {
    getEmployerRegistrationOptions: () => {
        console.log('API: Fetching employer registration options');
        return apiClient.get('/auth/employer-registration-options', {
            skipAuthHeader: true,
            skipAuthRefresh: true,
            skipIdleCheck: true
        });
    },

    register: (userData) => {
        console.log('API: Sending registration request to /auth/register');
        return apiClient.post('/auth/register', buildRegistrationPayload(userData), {
            headers: {
                'Idempotency-Key': createClientRequestId('employer-registration')
            },
            timeout: REGISTRATION_UPLOAD_TIMEOUT_MS,
            skipAuthHeader: true,
            skipAuthRefresh: true,
            skipIdleCheck: true,
            metadata: {
                operation: 'employer_registration',
                hasCompanyProfileDocument: isBrowserFile(userData.companyProfileDocument)
            }
        });
    },

    login: (credentials) => {
        console.log('API: Sending login request to /auth/login');
        return apiClient.post('/auth/login', credentials);
    },

    verifyLoginEmail: (payload) => {
        console.log('API: Verifying login email code');
        return apiClient.post('/auth/login/verify-email', payload);
    },

    resendLoginEmailVerification: (payload) => {
        console.log('API: Resending login email code');
        return apiClient.post('/auth/login/resend-email-verification', payload);
    },

    getContactVerificationStatus: () => {
        console.log('API: Fetching contact verification status');
        return apiClient.get('/auth/contact-verification');
    },

    verifyEmail: (payload) => {
        console.log('API: Verifying registration email code');
        return apiClient.post('/auth/verify-email', payload);
    },

    resendEmailVerification: (payload) => {
        console.log('API: Resending registration email code');
        return apiClient.post('/auth/resend-email-verification', payload);
    },

    requestPhoneVerification: (payload = {}) => {
        console.log('API: Requesting WhatsApp phone verification code');
        return apiClient.post('/auth/request-phone-verification', payload);
    },

    verifyPhone: (payload) => {
        console.log('API: Verifying WhatsApp phone code');
        return apiClient.post('/auth/verify-phone', payload);
    },

    logout: (refreshToken = getRefreshToken()) => {
        console.log('API: Sending logout request');
        return apiClient.post('/auth/logout', { refreshToken }, {
            skipAuthHeader: true,
            skipAuthRefresh: true,
            skipIdleCheck: true
        });
    },

    refreshToken: (refreshToken = getRefreshToken()) => {
        console.log('API: Refreshing token');
        return apiClient.post('/auth/refresh-token', { refreshToken }, {
            skipAuthHeader: true,
            skipAuthRefresh: true
        });
    },

    submitKYC: (documents) => {
        console.log('API: Submitting KYC documents');
        const formData = new FormData();
        Object.keys(documents).forEach(key => {
            if (documents[key] instanceof File) {
                formData.append(key, documents[key]);
            } else {
                formData.append(key, JSON.stringify(documents[key]));
            }
        });
        return apiClient.post('/employers/kyc/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    /**
 * Submit KYC for verification
 * @param {Object} options - Additional options
 */
    submitKycForVerification: (options = {}) => {
        console.log('API: Submitting KYC for verification');
        return apiClient.post('/employers/kyc/submit', {
            region: options.region
        });
    },

    forgotPassword: (email) => {
        console.log('API: Sending forgot password request');
        return apiClient.post('/auth/forgot-password', { email });
    },

    resetPassword: (payload) => {
        console.log('API: Sending reset password request');
        return apiClient.post('/auth/reset-password', payload);
    },

    // Get current user profile
    getProfile: () => {
        console.log('API: Fetching user profile');
        return apiClient.get('/auth/profile');
    },

    // Update user profile
    updateProfile: (profileData) => {
        console.log('API: Updating user profile');
        return apiClient.put('/auth/profile', profileData);
    },

    getDashboardGuideState: () => {
        console.log('API: Fetching dashboard guide state');
        return apiClient.get('/auth/dashboard-guide');
    },

    markDashboardGuideSeen: () => {
        console.log('API: Marking dashboard guide as seen');
        return apiClient.post('/auth/dashboard-guide/seen');
    }








    ,

    getKycStatus: () => {
        console.log('API: Fetching KYC status');
        return apiClient.get('/employers/kyc/status', {
            headers: { 'Cache-Control': 'no-cache' }
        });
    },


    getKycRequirements: (region) => {
        console.log(`API: Fetching KYC requirements for region ${region}`);
        return apiClient.get(`/employers/kyc/requirements/${region}`);
    },

    uploadKycDocuments: (files, documentTypeId, options = {}) => {
        console.log('API: Uploading KYC documents');
        const formData = buildKycDocumentsFormData(files, documentTypeId, options);

        return apiClient.post('/employers/kyc/documents', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: 30000 // 30 second timeout for file uploads
        });
    },
    getKycRegions: () => {
        console.log('API: Fetching available KYC regions');
        return apiClient.get('/employers/kyc/regions');
    },

};

export const legalAPI = {
    getPage: (documentType) => apiClient.get(`/legal-documents/employer/${documentType}`, {
        skipAuthHeader: true,
        skipAuthRefresh: true,
        skipIdleCheck: true
    })
};

export const supportTicketAPI = {
    list: (params = {}) => apiClient.get('/support/tickets', { params }),
    get: (ticketId) => apiClient.get(`/support/tickets/${ticketId}`),
    create: (payload, attachment = null) => apiClient.post('/support/tickets', buildSupportTicketFormData(payload, attachment)),
    addUpdate: (ticketId, payload, attachment = null) => apiClient.post(`/support/tickets/${ticketId}/updates`, buildSupportTicketFormData(payload, attachment))
};

export const employerRbacAPI = {
    getPermissions: () => {
        console.log('API: Fetching employer RBAC permissions');
        return apiClient.get('/employer-rbac/permissions');
    },

    getRoles: () => {
        console.log('API: Fetching employer roles');
        return apiClient.get('/employer-rbac/roles');
    },

    createRole: (roleData) => {
        console.log('API: Creating employer role');
        return apiClient.post('/employer-rbac/roles', roleData);
    },

    updateRole: (roleId, roleData) => {
        console.log(`API: Updating employer role ${roleId}`);
        return apiClient.put(`/employer-rbac/roles/${roleId}`, roleData);
    },

    deleteRole: (roleId) => {
        console.log(`API: Deleting employer role ${roleId}`);
        return apiClient.delete(`/employer-rbac/roles/${roleId}`);
    },

    getUsers: () => {
        console.log('API: Fetching employer users');
        return apiClient.get('/employer-rbac/users');
    },

    createUser: (userData) => {
        console.log('API: Creating employer user');
        return apiClient.post('/employer-rbac/users', userData);
    },

    updateUser: (userId, userData) => {
        console.log(`API: Updating employer user ${userId}`);
        return apiClient.put(`/employer-rbac/users/${userId}`, userData);
    },

    getAuditLogs: (params = {}) => {
        console.log('API: Fetching employer audit logs');
        return apiClient.get('/employer-rbac/audit-logs', { params });
    }
};



// Candidates API
export const candidateAPI = {
    getAll: (params) => {
        console.log('API: Fetching candidates with params:', params);
        return apiClient.get('/candidates', { params });
    },

    getEmployerCandidates: (params = {}) => {
        console.log('API: Fetching employer candidate pipeline with params:', params);
        return apiClient.get('/employers/candidates', { params });
    },

    getEmployerCandidate: (candidateId, params = {}) => {
        console.log(`API: Fetching employer candidate profile ${candidateId}`);
        return apiClient.get(`/employers/candidates/${candidateId}`, { params });
    },

    getById: (id) => {
        console.log(`API: Fetching candidate ${id}`);
        return apiClient.get(`/candidates/${id}`);
    },

    expressInterest: (candidateId, jobId) => {
        console.log(`API: Expressing interest in candidate ${candidateId} for job ${jobId}`);
        return apiClient.post(`/candidates/${candidateId}/express-interest`, { jobId });
    },

    getMatches: (filters) => {
        console.log('API: Fetching candidate matches');
        return apiClient.get('/candidates/matches', { params: filters });
    },

    updateMatchBands: (preferences) => {
        console.log('API: Updating match band preferences');
        return apiClient.put('/candidates/match-bands', preferences);
    }
};

// Interviews API
export const interviewAPI = {
    getAll: (params) => {
        console.log('API: Fetching interviews with params:', params);
        return apiClient.get('/interviews', { params });
    },

    schedule: (interviewData) => {
        console.log('API: Scheduling new interview');
        return apiClient.post('/interviews', interviewData);
    },

    scheduleForMatch: (jobId, matchId, interviewData) => {
        console.log(`API: Scheduling interview for match ${matchId}`);
        return apiClient.post(`/jobs/${jobId}/matches/${matchId}/interviews`, interviewData);
    },

    getForMatch: (jobId, matchId) => {
        console.log(`API: Fetching interviews for match ${matchId}`);
        return apiClient.get(`/jobs/${jobId}/matches/${matchId}/interviews`);
    },

    update: (id, interviewData) => {
        console.log(`API: Updating interview ${id}`);
        return apiClient.put(`/interviews/${id}`, interviewData);
    },

    confirm: (id) => {
        console.log(`API: Confirming interview ${id}`);
        return apiClient.post(`/interviews/${id}/confirm`, { userType: 'employer' });
    },

    cancel: (id, reason) => {
        console.log(`API: Cancelling interview ${id}`);
        return apiClient.post(`/interviews/${id}/cancel`, { reason });
    },

    saveFeedback: (id, feedbackData) => {
        console.log(`API: Saving interview feedback ${id}`);
        return apiClient.put(`/interviews/${id}/feedback`, feedbackData);
    },

    saveMatchFeedback: (jobId, matchId, interviewId, feedbackData) => {
        console.log(`API: Saving match interview feedback ${interviewId}`);
        return apiClient.put(`/jobs/${jobId}/matches/${matchId}/interviews/${interviewId}/employer-feedback`, feedbackData);
    },

    uploadAssessment: (assessmentData) => {
        console.log('API: Uploading assessment');
        const formData = new FormData();
        Object.keys(assessmentData).forEach(key => {
            if (assessmentData[key] instanceof File) {
                formData.append(key, assessmentData[key]);
            } else {
                formData.append(key, JSON.stringify(assessmentData[key]));
            }
        });
        return apiClient.post('/interviews/assessments', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    }
};

export const assessmentAPI = {
    getTypes: () => {
        console.log('API: Fetching assessment types');
        return apiClient.get('/assessments/types');
    },

    getEmployerAssessments: (params = {}) => {
        console.log('API: Fetching employer assessments', params);
        return apiClient.get('/assessments/employer', { params });
    },

    getJobAssessments: (jobId) => {
        console.log(`API: Fetching assessments for job ${jobId}`);
        return apiClient.get(`/jobs/${jobId}/assessments`);
    },

    createForJob: (jobId, assessmentData) => {
        console.log(`API: Creating assessment for job ${jobId}`);
        return apiClient.post(`/jobs/${jobId}/assessments`, assessmentData);
    },

    getForJob: (jobId, assessmentId) => {
        console.log(`API: Fetching assessment ${assessmentId}`);
        return apiClient.get(`/jobs/${jobId}/assessments/${assessmentId}`);
    },

    updateForJob: (jobId, assessmentId, assessmentData) => {
        console.log(`API: Updating assessment ${assessmentId}`);
        return apiClient.put(`/jobs/${jobId}/assessments/${assessmentId}`, assessmentData);
    },

    deactivate: (jobId, assessmentId) => {
        console.log(`API: Deactivating assessment ${assessmentId}`);
        return apiClient.delete(`/jobs/${jobId}/assessments/${assessmentId}`);
    },

    getResults: (jobId, assessmentId, params = {}) => {
        console.log(`API: Fetching assessment results ${assessmentId}`);
        return apiClient.get(`/jobs/${jobId}/assessments/${assessmentId}/results`, { params });
    },

    inviteCandidate: (jobId, matchId, assessmentId, inviteData) => {
        console.log(`API: Inviting match ${matchId} to assessment ${assessmentId}`);
        return apiClient.post(`/jobs/${jobId}/matches/${matchId}/assessments/${assessmentId}/invite`, inviteData);
    },

    inviteCandidates: (jobId, assessmentId, inviteData) => {
        console.log(`API: Bulk inviting candidates to assessment ${assessmentId}`);
        return apiClient.post(`/jobs/${jobId}/assessments/${assessmentId}/invitations`, inviteData);
    },

    reviewAttempt: (jobId, assessmentId, attemptId, reviewData) => {
        console.log(`API: Reviewing assessment attempt ${attemptId}`);
        return apiClient.put(`/jobs/${jobId}/assessments/${assessmentId}/attempts/${attemptId}/review`, reviewData);
    }
};

// Offers API
export const offerAPI = {
    getEmployerOffers: (params = {}) => {
        console.log('API: Fetching employer offers with params:', params);
        return apiClient.get('/offers/employer', { params });
    },

    getAll: (params = {}) => {
        console.log('API: Fetching employer offers with params:', params);
        return apiClient.get('/offers/employer', { params });
    },

    getCandidateOffers: (params = {}) => {
        console.log('API: Fetching candidate offers with params:', params);
        return apiClient.get('/offers/candidate', { params });
    },

    getDetails: (offerId) => {
        console.log(`API: Fetching offer ${offerId}`);
        return apiClient.get(`/offers/${offerId}`);
    },

    create: (offerData) => {
        console.log('API: Creating new offer');
        return apiClient.post('/offers', buildOfferFormData(offerData), {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    createForMatch: (jobId, matchId, offerData) => {
        console.log(`API: Creating offer for match ${matchId}`);
        return apiClient.post(`/jobs/${jobId}/matches/${matchId}/offers`, buildOfferFormData(offerData), {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    withdraw: (id, payload) => {
        console.log(`API: Withdrawing offer ${id}`);
        const body = typeof payload === 'string' ? { reason: payload } : payload;
        return apiClient.post(`/offers/${id}/withdraw`, body);
    },

    respondToNegotiation: (offerId, negotiationId, responseData) => {
        console.log(`API: Responding to negotiation ${negotiationId}`);
        return apiClient.post(`/offers/${offerId}/negotiations/${negotiationId}/respond`, responseData);
    },

    candidateRespond: (offerId, responseData) => {
        console.log(`API: Candidate responding to offer ${offerId}`);
        return apiClient.post(`/offers/${offerId}/respond`, buildOfferFormData(responseData), {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    submitSignedLetter: (offerId, payload) => {
        console.log(`API: Uploading signed letter for offer ${offerId}`);
        return apiClient.post(`/offers/${offerId}/signed-letter`, buildOfferFormData(payload), {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    getOnboardingTasks: (offerId) => {
        console.log(`API: Fetching onboarding tasks for offer ${offerId}`);
        return apiClient.get(`/offers/${offerId}/onboarding`);
    }
};

// Employer/Company Profile API
export const employerAPI = {
    // ================================================================
    // KYC RELATED ENDPOINTS
    // ================================================================

    /**
     * Get KYC status for current employer
     */
    getKycStatus: () => {
        console.log('API: Fetching KYC status');
        return apiClient.get('/employers/kyc/status', {
            headers: { 'Cache-Control': 'no-cache' }
        });
    },

    /**
     * Get KYC requirements for specific region
     * @param {string} region - Two-letter region code (e.g., 'US', 'UK', 'KE')
     */
    getKycRequirements: (region) => {
        console.log(`API: Fetching KYC requirements for region ${region}`);
        return apiClient.get(`/employers/kyc/requirements/${region}`);
    },

    /**
     * Upload KYC documents
     * @param {File|FileList} files - File(s) to upload
     * @param {number} documentTypeId - ID of the document type
     * @param {Object} options - Additional options
     */
    uploadKycDocuments: (files, documentTypeId, options = {}) => {
        console.log('API: Uploading KYC documents');
        const formData = buildKycDocumentsFormData(files, documentTypeId, options);

        return apiClient.post('/employers/kyc/documents', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: 30000 // 30 second timeout for file uploads
        });
    },

    getKycDocuments: () => {
        console.log('API: Fetching uploaded KYC documents');
        return apiClient.get('/employers/kyc/documents');
    },

    previewKycDocument: (documentId) => {
        console.log(`API: Previewing KYC document ${documentId}`);
        return apiClient.get(`/employers/kyc/documents/${documentId}/preview`, { responseType: 'blob' });
    },

    downloadKycDocument: (documentId) => {
        console.log(`API: Downloading KYC document ${documentId}`);
        return apiClient.get(`/employers/kyc/documents/${documentId}/download`, { responseType: 'blob' });
    },

    submitKycForVerification: (options = {}) => {
        console.log('API: Submitting KYC for verification');
        return apiClient.post('/employers/kyc/submit', {
            region: options.region
        });
    },





    // ================================================================
    // PROFILE MANAGEMENT
    // ================================================================

    /**
     * Get employer profile
     */
    getProfile: () => {
        console.log('API: Fetching employer profile');
        return apiClient.get('/employers/profile');
    },

    getProfileSetup: () => {
        console.log('API: Fetching employer profile setup');
        return apiClient.get('/employers/profile/setup');
    },

    getProfileEnrichment: () => apiClient.get('/employers/profile/enrichment'),

    getProfileEnrichmentEvents: (after = 0) => apiClient.get('/employers/profile/enrichment/events', {
        params: { after }
    }),

    acceptProfileEnrichmentProposal: (proposalId) => (
        apiClient.post(`/employers/profile/enrichment/proposals/${proposalId}/accept`)
    ),

    rejectProfileEnrichmentProposal: (proposalId) => (
        apiClient.post(`/employers/profile/enrichment/proposals/${proposalId}/reject`)
    ),

    acceptSafeProfileEnrichmentProposals: (runId) => (
        apiClient.post(`/employers/profile/enrichment/${runId}/accept-safe`)
    ),

    retryProfileEnrichment: (runId) => apiClient.post(`/employers/profile/enrichment/${runId}/retry`),

    cancelProfileEnrichment: (runId) => apiClient.post(`/employers/profile/enrichment/${runId}/cancel`),

    getProfileQuestions: () => {
        console.log('API: Fetching employer profile questions');
        return apiClient.get('/employers/profile/questions');
    },

    getProfileAnswers: () => {
        console.log('API: Fetching employer profile answers');
        return apiClient.get('/employers/profile/answers');
    },

    saveProfileAnswers: (answers) => {
        console.log('API: Saving employer profile answers');
        return apiClient.put('/employers/profile/answers', { answers });
    },

    /**
     * Create employer profile
     * @param {Object} profileData - Profile information
     */
    createProfile: (profileData) => {
        console.log('API: Creating employer profile');
        return apiClient.post('/employers/profile', profileData);
    },

    /**
     * Update employer profile
     * @param {Object} profileData - Updated profile information
     */
    updateProfile: (profileData) => {
        console.log('API: Updating employer profile');
        return apiClient.put('/employers/profile', profileData);
    },

    /**
     * Upload company logo
     * @param {File} file - Logo file
     */
    uploadLogo: (file) => {
        console.log('API: Uploading company logo');
        const formData = new FormData();
        formData.append('logo', file);
        return apiClient.post('/employers/profile/logo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    getProfileSections: () => {
        console.log('API: Fetching employer profile sections');
        return apiClient.get('/employers/profile/sections');
    },

    updateProfileSections: (sections) => {
        console.log('API: Updating employer profile sections');
        return apiClient.put('/employers/profile/sections', { sections });
    },

    deleteProfileSection: (sectionId) => {
        console.log(`API: Deleting employer profile section ${sectionId}`);
        return apiClient.delete(`/employers/profile/sections/${sectionId}`);
    }
};

// Company Profile API (for backward compatibility)
export const companyAPI = {
    getProfile: () => employerAPI.getProfile(),
    updateProfile: (profileData) => employerAPI.updateProfile(profileData),
    uploadLogo: (file) => employerAPI.uploadLogo(file)
};

// Dashboard API
export const dashboardAPI = {
    getOverview: (timeframe = '90d') => {
        console.log('API: Fetching employer dashboard overview');
        return apiClient.get('/dashboard/overview', {
            params: { timeframe }
        });
    },

    getNotifications: (params = {}) => {
        return apiClient.get('/dashboard/notifications', { params });
    },

    markNotificationRead: (notificationId) => {
        return apiClient.patch(`/dashboard/notifications/${notificationId}/read`);
    },

    markAllNotificationsRead: () => {
        return apiClient.patch('/dashboard/notifications/read-all');
    },

    getStats: () => {
        console.log('API: Fetching dashboard stats');
        return apiClient.get('/dashboard/stats');
    },

    getRecentActivity: () => {
        console.log('API: Fetching recent activity');
        return apiClient.get('/dashboard/activity');
    },

    getAnalytics: (dateRange) => {
        console.log('API: Fetching analytics for date range:', dateRange);
        return apiClient.get('/dashboard/analytics', {
            params: { dateRange }
        });
    }
};


export const jobAPI = {
    getAll: (params) => {
        console.log('API: Fetching jobs with params:', params);
        return apiClient.get('/jobs', { params });
    },

    // Get jobs for the current employer
    getEmployerJobs: (params = {}) => {
        console.log('API: Fetching employer jobs with params:', params);
        return apiClient.get('/jobs/employer', { params });
    },

    getById: (id) => {
        console.log(`API: Fetching job ${id}`);
        return apiClient.get(`/jobs/${id}`);
    },

    create: (jobData) => {
        console.log('API: Creating new job');
        return apiClient.post('/jobs', jobData);
    },

    createImport: (file, options = {}) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('notifyEmail', String(Boolean(options.notifyEmail)));

        return apiClient.post('/jobs/imports', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Idempotency-Key': options.idempotencyKey || createClientRequestId('job-import')
            },
            timeout: JOB_IMPORT_UPLOAD_TIMEOUT_MS,
            onUploadProgress: options.onUploadProgress,
            metadata: {
                operation: 'job_document_import',
                file: {
                    name: file?.name || null,
                    type: file?.type || null,
                    size: file?.size || 0
                }
            }
        });
    },

    getActionableImports: () => apiClient.get('/jobs/imports/actionable'),

    getImport: (runId) => apiClient.get(`/jobs/imports/${runId}`),

    getImportEvents: (after = 0, limit = 100) => apiClient.get('/jobs/imports/events', {
        params: { after, limit }
    }),

    retryImport: (runId) => apiClient.post(`/jobs/imports/${runId}/retry`),

    cancelImport: (runId) => apiClient.post(`/jobs/imports/${runId}/cancel`),

    update: (id, jobData) => {
        console.log(`API: Updating job ${id}`);
        return apiClient.put(`/jobs/${id}`, jobData);
    },

    updateStatus: (id, status, reason) => {
        console.log(`API: Updating job ${id} status`);
        return apiClient.patch(`/jobs/${id}/status`, { status, reason });
    },

    delete: (id) => {
        console.log(`API: Deleting job ${id}`);
        return apiClient.delete(`/jobs/${id}`);
    },

    publish: (id) => {
        console.log(`API: Publishing job ${id}`);
        return apiClient.post(`/jobs/${id}/publish`);
    },

    unpublish: (id) => {
        console.log(`API: Unpublishing job ${id}`);
        return apiClient.post(`/jobs/${id}/unpublish`);
    },

    getMatches: (id, filters) => {
        console.log(`API: Fetching matches for job ${id}`);
        return apiClient.get(`/jobs/${id}/matches`, { params: filters });
    },
     getImmediateMatches: (id, params = {}) => {
        console.log(`API: Fetching immediate matches for job ${id}`);
        return apiClient.get(`/jobs/${id}/immediate-matches`, { params });
    },

    getMatchDetails: (jobId, matchId) => {
        console.log(`API: Fetching match ${matchId} for job ${jobId}`);
        return apiClient.get(`/jobs/${jobId}/matches/${matchId}`);
    },

    takeMatchAction: (jobId, matchId, actionData) => {
        console.log(`API: Taking action on match ${matchId}`);
        return apiClient.post(`/jobs/${jobId}/matches/${matchId}/action`, actionData);
    },

    bulkMatchAction: (jobId, actionData) => {
        console.log(`API: Taking bulk match action for job ${jobId}`);
        return apiClient.post(`/jobs/${jobId}/matches/bulk-action`, actionData);
    },

    triggerMatching: (id, options = {}) => {
        console.log(`API: Triggering matching for job ${id}`);
        return apiClient.post(`/jobs/${id}/trigger-matching`, options);
    },

    getProfileSetup: (id) => {
        console.log(`API: Fetching profile setup for job ${id}`);
        return apiClient.get(`/jobs/${id}/profile/setup`);
    },

    getProfileQuestions: (id) => {
        console.log(`API: Fetching profile questions for job ${id}`);
        return apiClient.get(`/jobs/${id}/profile/questions`);
    },

    getProfileAnswers: (id) => {
        console.log(`API: Fetching profile answers for job ${id}`);
        return apiClient.get(`/jobs/${id}/profile/answers`);
    },

    saveProfileAnswers: (id, answers) => {
        console.log(`API: Saving profile answers for job ${id}`);
        return apiClient.put(`/jobs/${id}/profile/answers`, { answers });
    },

    getAnalytics: (params = {}) => {
        console.log('API: Fetching job analytics');
        return apiClient.get('/jobs/employer/analytics', { params });
    },

    getJobAnalytics: (id, params = {}) => {
        console.log(`API: Fetching analytics for job ${id}`);
        return apiClient.get(`/jobs/${id}/analytics`, { params });
    },

    getMatchingPreferences: () => {
        console.log('API: Fetching matching preferences');
        return apiClient.get('/jobs/matching/preferences');
    },

    updateMatchingPreferences: (preferences) => {
        console.log('API: Updating matching preferences');
        return apiClient.put('/jobs/matching/preferences', preferences);
    },

    getMatchingConfiguration: () => {
        console.log('API: Fetching matching configuration');
        return apiClient.get('/jobs/matching/configuration');
    },

    updateMatchingConfiguration: (configuration) => {
        console.log('API: Updating matching configuration');
        return apiClient.put('/jobs/matching/configuration', configuration);
    },

    getTemplates: () => {
        console.log('API: Fetching job templates');
        return apiClient.get('/jobs/templates');
    },


    parseJobDescription: (file) => {
        console.log('API: Parsing job description file');
        const requestId = createClientRequestId('job-parse');
        const fileMetadata = {
            name: file?.name || null,
            type: file?.type || null,
            size: file?.size || 0,
            lastModified: file?.lastModified || null
        };
        logApiJson('job_document_parse_request_prepared', {
            requestId,
            file: fileMetadata,
            endpoint: '/jobs/parse-jd'
        });
        const formData = new FormData();
        formData.append('file', file);
        return apiClient.post('/jobs/parse-jd', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'X-Request-Id': requestId
            },
            metadata: {
                requestId,
                operation: 'job_document_parse',
                file: fileMetadata
            }
        });
    },

    getAssessments: (jobId) => {
        console.log(`API: Fetching assessments for job ${jobId}`);
        return assessmentAPI.getJobAssessments(jobId);
    },

    createAssessment: (jobId, assessmentData) => {
        console.log(`API: Creating assessment for job ${jobId}`);
        return assessmentAPI.createForJob(jobId, assessmentData);
    },

    updateAssessment: (jobId, assessmentId, assessmentData) => {
        console.log(`API: Updating assessment ${assessmentId}`);
        return assessmentAPI.updateForJob(jobId, assessmentId, assessmentData);
    },

    deleteAssessment: (jobId, assessmentId) => {
        console.log(`API: Deleting assessment ${assessmentId}`);
        return assessmentAPI.deactivate(jobId, assessmentId);
    },

    getAssessmentResults: (jobId, assessmentId) => {
        console.log(`API: Fetching assessment results ${assessmentId}`);
        return assessmentAPI.getResults(jobId, assessmentId);
    },

    inviteAssessmentCandidate: (jobId, matchId, assessmentId, inviteData) => {
        console.log(`API: Inviting candidate to assessment ${assessmentId}`);
        return assessmentAPI.inviteCandidate(jobId, matchId, assessmentId, inviteData);
    }
};
